/**
 * UserRoleOverridePage (L13.13) — sous-page admin pour promouvoir/rétrograder
 * un utilisateur (rôle + service), avec ou sans date d'expiration.
 *
 * Cas d'usage :
 *   - Remplacement vacances (agent → manager 15-30 août)
 *   - Départ retraite (manager → agent jusqu'au départ)
 *   - Promotion définitive (sans expiresAt)
 *
 * Accessible depuis /admin/utilisateurs/:id (lien dans la page détail user).
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import {
  Card, Button, Badge, Alert, LogoMark, SectionEyebrow,
} from '@/components/ui';
import {
  UserCog, ArrowLeft, AlertCircle, CheckCircle2, X, Calendar, RotateCcw, Loader2,
} from 'lucide-react';

type RoleType = 'super_admin' | 'admin' | 'manager' | 'agent' | 'administre';
type ServiceType =
  | 'service_technique' | 'scolaire_cantine' | 'petite_enfance'
  | 'urbanisme' | 'associations' | 'grc' | 'rh' | 'comptable';

interface RoleOverrideDTO {
  id: string;
  userId: string;
  userFullName: string;
  userEmail: string;
  originalRole: RoleType;
  originalService: ServiceType | null;
  newRole: RoleType;
  newService: ServiceType | null;
  expiresAt: string | null;
  reason: string | null;
  grantedBy: string;
  grantedByName: string;
  grantedAt: string;
  revokedAt: string | null;
  revokedReason: string | null;
  isActive: boolean;
}

const ROLE_LABELS: Record<RoleType, string> = {
  super_admin: 'Super admin',
  admin: 'Admin (maire/adjoint)',
  manager: 'Manager',
  agent: 'Agent',
  administre: 'Administré',
};
const SERVICE_LABELS: Record<ServiceType, string> = {
  service_technique: 'Service technique',
  scolaire_cantine: 'Scolaire / cantine',
  petite_enfance: 'Petite enfance',
  urbanisme: 'Urbanisme',
  associations: 'Associations',
  grc: 'GRC / accueil',
  rh: 'Ressources humaines',
  comptable: 'Comptabilité',
};

const ROLES_SELECTABLE: RoleType[] = ['manager', 'agent', 'admin'];
const SERVICES_SELECTABLE: ServiceType[] = [
  'service_technique', 'scolaire_cantine', 'petite_enfance', 'urbanisme',
  'associations', 'grc', 'rh', 'comptable',
];

export function UserRoleOverridePage() {
  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { baseRole } = useAuth();

  // L13.14 — garde de gouvernance : seul un admin/super_admin NATIF accède.
  const canGrantRoles = baseRole === 'admin' || baseRole === 'super_admin';

  const [history, setHistory] = useState<RoleOverrideDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Formulaire création override
  const [showForm, setShowForm] = useState(false);
  const [newRole, setNewRole] = useState<RoleType>('manager');
  const [newService, setNewService] = useState<ServiceType>('service_technique');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  // Confirmation révocation
  const [confirmRevoke, setConfirmRevoke] = useState<RoleOverrideDTO | null>(null);
  const [revokeReason, setRevokeReason] = useState('');

  async function reload() {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.roleOverrides.forUser(userId);
      setHistory((res.items ?? []) as RoleOverrideDTO[]);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submitOverride() {
    if (!userId) return;
    setActionError(null);
    setSubmitting(true);
    try {
      const needsService = newRole === 'manager' || newRole === 'agent';
      await api.admin.roleOverrides.create(userId, {
        newRole,
        newService: needsService ? newService : null,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        reason: reason.trim() || undefined,
      });
      setActionSuccess(
        expiresAt
          ? `Promotion temporaire jusqu'au ${new Date(expiresAt).toLocaleDateString('fr-FR')} appliquée.`
          : `Changement de rôle définitif appliqué.`
      );
      setShowForm(false);
      setReason('');
      setExpiresAt('');
      void reload();
      setTimeout(() => setActionSuccess(null), 5000);
    } catch (err) {
      setActionError(err instanceof ApiException ? err.payload.error : 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitRevoke() {
    if (!confirmRevoke) return;
    setActionError(null);
    setSubmitting(true);
    try {
      await api.admin.roleOverrides.revoke(confirmRevoke.id, revokeReason || 'Révocation manuelle');
      setActionSuccess('Override révoqué. Rôle restauré à sa valeur d\'origine.');
      setConfirmRevoke(null);
      setRevokeReason('');
      void reload();
      setTimeout(() => setActionSuccess(null), 5000);
    } catch (err) {
      setActionError(err instanceof ApiException ? err.payload.error : 'Erreur lors de la révocation');
    } finally {
      setSubmitting(false);
    }
  }

  const activeOverride = history.find((h) => h.isActive);

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(`/admin/utilisateurs/${userId}`)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-r-md border border-border bg-surface/60 text-ink-muted hover:bg-surface/80 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          aria-label="Retour"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <LogoMark icon={UserCog} accent="tech" size="md" />
        <div>
          <SectionEyebrow as="div">Administration</SectionEyebrow>
          <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
            Changement de rôle temporaire ou définitif
          </h1>
        </div>
      </div>

      {/* L13.14 — accès réservé aux admins natifs : un compte promu ne promeut pas */}
      {!canGrantRoles ? (
        <Alert variant="error" icon={AlertCircle}>
          La promotion ou rétrogradation de rôle est réservée au maire, aux adjoints
          ou à l'administrateur technique. Un rôle obtenu par promotion temporaire
          ne permet pas de gérer les rôles des autres comptes.
        </Alert>
      ) : (
        <>
      {actionSuccess && <Alert variant="success" icon={CheckCircle2}>{actionSuccess}</Alert>}
      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}

      {/* État actuel */}
      {loading ? (
        <Card variant="glass" padded>
          <div className="flex items-center gap-2 text-sm text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement…
          </div>
        </Card>
      ) : activeOverride ? (
        <Card variant="glass" padded>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-r-lg bg-warning/20 text-warning border border-warning/40" aria-hidden="true">
              <RotateCcw className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-base font-semibold text-ink">
                Override actif
              </h2>
              <p className="mt-1 text-sm text-ink-muted">
                <strong className="text-ink">{activeOverride.userFullName}</strong> ({activeOverride.userEmail})
              </p>
              <div className="mt-2 grid sm:grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Rôle d'origine</div>
                  <div className="mt-0.5 text-ink">{ROLE_LABELS[activeOverride.originalRole]}{activeOverride.originalService ? ` · ${SERVICE_LABELS[activeOverride.originalService]}` : ''}</div>
                </div>
                <div>
                  <div className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Rôle actuel</div>
                  <div className="mt-0.5 text-ink font-semibold">{ROLE_LABELS[activeOverride.newRole]}{activeOverride.newService ? ` · ${SERVICE_LABELS[activeOverride.newService]}` : ''}</div>
                </div>
              </div>
              {activeOverride.expiresAt && (
                <p className="mt-2 text-2xs text-ink-subtle inline-flex items-center gap-1">
                  <Calendar className="h-3 w-3" aria-hidden="true" />
                  Expire le {new Date(activeOverride.expiresAt).toLocaleString('fr-FR')} (révocation auto)
                </p>
              )}
              {!activeOverride.expiresAt && (
                <Badge variant="info" size="sm" className="mt-2">Changement définitif</Badge>
              )}
              {activeOverride.reason && (
                <p className="mt-2 text-2xs text-ink-muted italic">"{activeOverride.reason}"</p>
              )}
              <p className="mt-2 text-2xs text-ink-subtle">
                Accordé par {activeOverride.grantedByName} le {new Date(activeOverride.grantedAt).toLocaleString('fr-FR')}
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { setConfirmRevoke(activeOverride); setRevokeReason(''); }}
                className="mt-3"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
                Révoquer maintenant (restaure le rôle d'origine)
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        !showForm && (
          <Card variant="glass" padded>
            <div className="text-center py-6">
              <UserCog className="mx-auto h-10 w-10 text-ink-dim" aria-hidden="true" />
              <h2 className="mt-2 font-display text-base font-semibold text-ink">
                Aucun override actif pour cet utilisateur
              </h2>
              <p className="mt-1 text-sm text-ink-muted">
                Tu peux promouvoir/rétrograder temporairement ou définitivement.
              </p>
              <Button variant="primary" size="sm" onClick={() => setShowForm(true)} className="mt-4">
                Nouveau changement de rôle
              </Button>
            </div>
          </Card>
        )
      )}

      {/* Formulaire création */}
      {showForm && !activeOverride && (
        <Card variant="glass" padded>
          <h2 className="font-display text-base font-semibold text-ink">Nouveau changement de rôle</h2>
          {actionError && <Alert variant="error" icon={AlertCircle} className="mt-3">{actionError}</Alert>}

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Nouveau rôle</label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as RoleType)}
                className="mt-1 w-full h-10 rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-sm text-ink shadow-soft focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg"
              >
                {ROLES_SELECTABLE.map((r) => (
                  <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                ))}
              </select>
            </div>
            {(newRole === 'manager' || newRole === 'agent') && (
              <div>
                <label className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Service</label>
                <select
                  value={newService}
                  onChange={(e) => setNewService(e.target.value as ServiceType)}
                  className="mt-1 w-full h-10 rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-sm text-ink shadow-soft focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg"
                >
                  {SERVICES_SELECTABLE.map((s) => (
                    <option key={s} value={s}>{SERVICE_LABELS[s]}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="mt-3">
            <label className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
              Date d'expiration (laisser vide pour un changement définitif)
            </label>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="mt-1 w-full h-10 rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-sm text-ink shadow-soft focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg"
            />
            <p className="mt-1 text-2xs text-ink-subtle">
              Si renseignée, un cron révoque automatiquement à cette date et restaure le rôle d'origine.
            </p>
          </div>

          <div className="mt-3">
            <label className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Motif (optionnel)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Ex: Remplacement vacances Marie 15-30 août"
              className="mt-1 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg"
            />
          </div>

          <div className="mt-4 flex gap-2 justify-end">
            <Button variant="secondary" size="sm" onClick={() => setShowForm(false)}>Annuler</Button>
            <Button variant="primary" size="sm" onClick={submitOverride} loading={submitting}>
              Appliquer
            </Button>
          </div>
        </Card>
      )}

      {/* Historique */}
      <Card variant="glass" padded={false}>
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-display text-base font-semibold text-ink">Historique des overrides</h2>
        </div>
        {history.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-ink-muted">
            Aucun override enregistré pour cet utilisateur.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {history.map((o) => (
              <li key={o.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {o.isActive ? (
                        <Badge variant="warning" size="sm">Actif</Badge>
                      ) : o.revokedAt ? (
                        <Badge variant="neutral" size="sm">Révoqué</Badge>
                      ) : (
                        <Badge variant="neutral" size="sm">Expiré</Badge>
                      )}
                      <span className="text-sm text-ink">
                        {ROLE_LABELS[o.originalRole]} → <strong>{ROLE_LABELS[o.newRole]}</strong>
                      </span>
                    </div>
                    {o.reason && (
                      <p className="mt-1 text-2xs text-ink-muted italic">"{o.reason}"</p>
                    )}
                    <p className="mt-1 text-2xs text-ink-subtle">
                      Accordé par {o.grantedByName} le {new Date(o.grantedAt).toLocaleString('fr-FR')}
                      {o.expiresAt && ` · Expire ${new Date(o.expiresAt).toLocaleString('fr-FR')}`}
                      {o.revokedAt && ` · Révoqué ${new Date(o.revokedAt).toLocaleString('fr-FR')} (${o.revokedReason})`}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Dialog R13 confirmation révocation */}
      {confirmRevoke && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
        >
          <button
            type="button"
            aria-label="Fermer"
            onClick={() => setConfirmRevoke(null)}
            className="absolute inset-0 bg-bg/70 backdrop-blur-sm"
          />
          <div className="relative z-10 w-full max-w-md rounded-r-2xl border border-border bg-surface/95 backdrop-blur-glass shadow-glass-hi p-6">
            <h2 className="font-display text-lg font-semibold text-ink">
              Révoquer l'override maintenant ?
            </h2>
            <p className="mt-2 text-sm text-ink-muted">
              Le rôle de <strong>{confirmRevoke.userFullName}</strong> sera restauré à
              <strong className="ml-1">{ROLE_LABELS[confirmRevoke.originalRole]}</strong>
              {confirmRevoke.originalService && (
                <> · <strong>{SERVICE_LABELS[confirmRevoke.originalService]}</strong></>
              )}.
            </p>
            <label className="mt-4 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
              Motif de révocation
            </label>
            <textarea
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Ex: retour de vacances anticipé"
              className="mt-1 w-full rounded-r-lg border border-border bg-bg-2/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg"
            />
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="secondary" size="sm" onClick={() => setConfirmRevoke(null)}>Annuler</Button>
              <Button variant="primary" size="sm" onClick={submitRevoke} loading={submitting}>
                Révoquer
              </Button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
